import React, { useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import MessageContent from './MessageContent';
import MessageToolUse from './MessageToolUse';
import MessageActions from './MessageActions';
import ImageRenderer from '../ImageRenderer';
import ReferenceMarker from './ReferenceMarker';
import { processMessageForInlineRendering } from '../utils/messageUtils';

// Separate stable image box component that never re-renders once mounted
const StableImageBox = React.memo(({ image }) => {
  return (
    <Box 
      sx={{ 
        my: 2,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: '90%',
          width: 'fit-content',
          p: 2,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ImageRenderer
            filename={image.filename}
            alt={image.alt || 'Generated image'}
          />
        </Box>
        {image.alt && (
          <Box 
            sx={{ 
              mt: 1.5,
              px: 1,
              width: '100%',
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                fontStyle: 'italic',
                textAlign: 'center',
                display: 'block',
                lineHeight: 1.4,
              }}
            >
              {image.alt}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Never re-render once filename is set and alt is the same
  return prevProps.image.filename === nextProps.image.filename &&
         prevProps.image.alt === nextProps.image.alt;
});

const ChatMessage = ({
  message,
  model,
  showReferences,
  onFeedback,
  feedbackGiven,
  copyFeedback,
  onCopy
}) => {
  // Use refs to store stable references to images once they're loaded
  const stableImagesRef = useRef([]);
  const imageFilenamesSetRef = useRef(new Set());
  const [stableImages, setStableImages] = useState([]);
  // Track which image boxes we've already rendered
  const renderedImageBoxesRef = useRef(new Set());

  const { processedText, references, toolUse, segments } = useMemo(() => {
    let processedText = '';
    let references = [];
    let toolUse = message.toolUse || [];
    let segments = [];

    if (message.sender === 'ai') {
      let textToProcess = message.text;

      if (model.includes('DeepSeek')) {
        if (message.responseContent) {
          textToProcess = message.responseContent;
        } else if (message.text.includes('</think>')) {
          const parts = message.text.split('</think>');
          textToProcess = parts.length > 1 ? parts[1] : '';
        }
      }

      const isComplete = !message.isThinking && textToProcess.length > 0;
      const shouldShowReferences = showReferences && isComplete;

      const processed = processMessageForInlineRendering(textToProcess, shouldShowReferences);
      processedText = processed.processedText;
      references = processed.references;
      
      if (processed.tools && processed.tools.length > 0) {
        const existingToolNames = new Set(toolUse.map(t => t.tool_name));
        for (const tool of processed.tools) {
          if (!existingToolNames.has(tool.tool_name)) {
            toolUse = [...toolUse, tool];
          } else {
            toolUse = toolUse.map(t => {
              if (t.tool_name === tool.tool_name) {
                return {
                  ...t,
                  ...tool,
                  start_time: tool.start_time || t.start_time,
                  end_time: tool.end_time || t.end_time,
                  description: tool.description || t.description,
                  status: tool.status || t.status,
                  completed: tool.completed || t.completed
                };
              }
              return t;
            });
          }
        }
      }
      
      const extractedImages = processed.images || [];
      let hasNewImages = false;
      
      for (const img of extractedImages) {
        if (!imageFilenamesSetRef.current.has(img.filename)) {
          hasNewImages = true;
          imageFilenamesSetRef.current.add(img.filename);
          stableImagesRef.current = [...stableImagesRef.current, img];
        }
      }
      
      if (hasNewImages) {
        setTimeout(() => {
          setStableImages([...stableImagesRef.current]);
        }, 0);
      }

      const parts = processedText.split(/(\[IMAGE_\d+\]|\[\^\d+\])/g);
      segments = parts.map((part, index) => {
        const imageMatch = part.match(/\[IMAGE_(\d+)\]/);
        if (imageMatch) {
          const imgIndex = parseInt(imageMatch[1]);
          return {
            type: 'image',
            imageIndex: imgIndex,
            key: `image-${imgIndex}-${stableImagesRef.current[imgIndex]?.filename || 'loading'}`
          };
        }
        const refMatch = part.match(/\[\^(\d+)\]/);
        if (refMatch) {
          const refIndex = parseInt(refMatch[1]);
          return {
            type: 'reference',
            referenceIndex: refIndex,
            key: `ref-${refIndex}-${index}`
          };
        }
        return {
          type: 'text',
          content: part,
          key: `text-${index}`
        };
      }).filter(segment => segment.type === 'image' || segment.type === 'reference' || (segment.type === 'text' && segment.content.trim()));

      if (processed.messageId && !message.messageId) {
        message.messageId = processed.messageId;
      }
    } else {
      processedText = message.text;
      segments = [{ type: 'text', content: message.text, key: 'text-0' }];
    }

    return { processedText, references, toolUse, segments };
  }, [message.text, message.responseContent, message.toolUse, message.isThinking, model, showReferences]);
  
  // Render segments as separate boxes (text boxes and image boxes)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
        position: 'relative',
        mb: message.sender === 'ai' ? 3 : 1,
        width: '100%',
      }}
    >
      {message.sender === 'ai' && segments && segments.length > 0 ? (
        // Render AI message as separate text and image segments
        <>
          {segments.map((segment, idx) => {
            if (segment.type === 'image') {
              // Render image box - completely separate and stable
              const image = stableImages[segment.imageIndex];
              if (!image) return null; // Wait for image to be in state

              return (
                <StableImageBox
                  key={segment.key}
                  image={image}
                />
              );
            } else if (segment.type === 'reference') {
              // Render reference marker inline
              return (
                <ReferenceMarker
                  key={segment.key}
                  referenceIndex={segment.referenceIndex}
                  referenceText={references[segment.referenceIndex]}
                />
              );
            } else {
              // Render text segment
              return (
                <Paper
                  key={segment.key}
                  elevation={1}
                  sx={{
                    maxWidth: '100%',
                    width: '100%',
                    p: 2.5,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 2,
                    alignSelf: 'flex-start',
                    position: 'relative',
                    mb: idx < segments.length - 1 ? 0 : 0, // No margin, segments flow together
                  }}
                >
                  <MessageContent
                    message={message}
                    model={model}
                    processedText={segment.content}
                    references={references}
                    images={[]} // No images in text segments
                    toolUse={idx === 0 ? toolUse : []} // Only show tools on first segment
                  />
                </Paper>
              );
            }
          })}
        </>
      ) : (
        // Render user message or AI message without segments
        <Paper
          elevation={1}
          sx={{
            maxWidth: message.sender === 'user' ? '80%' : '100%',
            width: message.sender === 'user' ? 'auto' : '100%',
            p: 2.5,
            bgcolor: message.sender === 'user' ? 'primary.main' : 'background.paper',
            color: message.sender === 'user' ? 'black' : 'text.primary',
            borderRadius: 2,
            alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
            position: 'relative',
          }}
        >
          <MessageContent
            message={message}
            model={model}
            processedText={processedText}
            references={references}
            images={[]}
            toolUse={toolUse}
          />
        </Paper>
      )}
      
      {message.sender === 'ai' && (
        <MessageActions
          messageId={message.messageId || message.id}
          onFeedback={onFeedback}
          feedbackGiven={feedbackGiven}
          copyFeedback={copyFeedback}
          onCopy={(msgId) => onCopy(message.text, msgId)}
        />
      )}
    </Box>
  );
};

export default ChatMessage;

